import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Check, Trash2, ArrowRight } from "lucide-react";
import api, { formatMoney } from "../../lib/api";

const fmt = (v) => (v == null ? "—" : Math.abs(v) >= 100 ? formatMoney(v) : String(Math.round(v * 100) / 100).replace(".", ","));

export function AccionesPanel() {
  const [acc, setAcc] = useState(null);
  const [actual, setActual] = useState({});
  const [verHechas, setVerHechas] = useState(false);
  const load = () => api.get("/acciones").then((r) => setAcc(r.data.acciones)).catch(() => setAcc([]));
  useEffect(() => {
    load();
    api.get("/indicadores/stock").then((r) => {
      const m = {};
      (r.data.grupos || []).forEach((g) => g.items.forEach((i) => { m[i.key] = i.value; }));
      setActual(m);
    }).catch(() => {});
  }, []);
  if (!acc) return null;
  const lista = acc.filter((a) => (verHechas ? a.estado === "hecha" : a.estado === "abierta"));
  const cerrar = async (a) => {
    await api.patch(`/acciones/${a.id}`, { estado: "hecha", valor_final: actual[a.indicador] ?? null });
    toast.success("Acción marcada como hecha");
    load();
  };
  const borrar = async (a) => { await api.delete(`/acciones/${a.id}`); load(); };

  return (
    <div className="card p-5 mb-6" data-testid="acciones-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><ClipboardCheck size={18} className="text-emerald-600" /> Acciones en marcha</h3>
          <p className="text-xs text-slate-500">Cada alerta de Indicadores de stock se puede convertir en una acción con responsable y plazo. Aquí ves si el dato mejora.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
          {[[false, "Abiertas"], [true, "Hechas"]].map(([k, l]) => (
            <button key={l} onClick={() => setVerHechas(k)} data-testid={`acciones-ver-${k ? "hechas" : "abiertas"}`}
                    className={"px-3 py-1.5 text-xs font-semibold rounded-md " + (verHechas === k ? "bg-slate-900 text-white" : "text-slate-600")}>{l}</button>
          ))}
        </div>
      </div>
      {!lista.length ? (
        <p className="text-sm text-slate-500" data-testid="acciones-vacio">{verHechas ? "Aún no has cerrado ninguna acción." : <>No hay acciones abiertas. Crea una desde <Link to="/indicadores/stock" className="text-sky-700 underline">Indicadores de stock</Link> con el botón «Acción».</>}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="dense w-full" data-testid="acciones-tabla">
            <thead><tr><th>Alerta</th><th>Decisión</th><th>Responsable</th><th>Plazo</th><th className="num">Al crearla</th><th className="num">{verHechas ? "Al cerrarla" : "Ahora"}</th><th className="num">Cambio</th><th /></tr></thead>
            <tbody>{lista.map((a) => {
              const ahora = verHechas ? a.valor_final : actual[a.indicador];
              const delta = a.valor_inicial != null && ahora != null ? ahora - a.valor_inicial : null;
              const vencida = !verHechas && a.fecha_limite && a.fecha_limite < new Date().toISOString().slice(0, 10);
              return (
                <tr key={a.id} data-testid={`accion-${a.id}`}>
                  <td className="font-semibold">{a.enlace ? <Link to={a.enlace} className="hover:underline inline-flex items-center gap-1">{a.indicador_label} <ArrowRight size={11} /></Link> : a.indicador_label}
                    {a.nota && <div className="text-[11px] text-slate-500 font-normal">{a.nota}</div>}</td>
                  <td>{a.decision}</td><td>{a.responsable || "Titular"}</td>
                  <td className={vencida ? "text-red-600 font-semibold" : ""}>{a.fecha_limite ? new Date(a.fecha_limite).toLocaleDateString("es-ES") : "—"}{vencida ? " · vencida" : ""}</td>
                  <td className="num">{fmt(a.valor_inicial)}</td><td className="num">{fmt(ahora)}</td>
                  <td className={"num font-bold " + (delta == null ? "" : delta <= 0 ? "text-emerald-700" : "text-red-600")}>{delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmt(delta)}`}</td>
                  <td className="whitespace-nowrap">
                    {!verHechas && <button onClick={() => cerrar(a)} className="text-emerald-600 hover:text-emerald-800 mr-2" title="Marcar como hecha" data-testid={`accion-hecha-${a.id}`}><Check size={15} /></button>}
                    <button onClick={() => borrar(a)} className="text-red-400 hover:text-red-600" title="Borrar" data-testid={`accion-borrar-${a.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-2">«Ahora» usa el último estudio subido: sube un inventario nuevo para medir el efecto. En las alertas de dinero parado, bajar es mejorar.</p>
        </div>
      )}
    </div>
  );
}
